import { useMemo, useState } from 'react';
import { Check, ChevronDown, Plus, SearchIcon, X } from 'lucide-react';
import type { LabMarkerTVitalLabMarkerMetadataT as LabMarkerT } from '@lotushealth/lotushealth_js_sdk/schemas';

import { Button } from '@/shared/ui/shadcn/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/shared/ui/shadcn/collapsible';
import { Input } from '@/shared/ui/shadcn/input';

import type { NativeLabOrderSelectionItem } from '../../model/nativeLabOrderSelection.js';
import { useBiomarkerSearch } from '../../model/useBiomarkerSearch.js';

export interface NativeLabOrderSelectionStepProps {
  selectedItems: NativeLabOrderSelectionItem[];
  onAddItem: (item: NativeLabOrderSelectionItem & { source: 'marker' }) => void;
  onRemoveItem: (item: NativeLabOrderSelectionItem) => void;
}

function Contents({ item }: { item: NativeLabOrderSelectionItem }) {
  const [open, setOpen] = useState(false);
  if (item.components.length === 0 || (item.kind !== 'panel' && item.kind !== 'configured_test')) {
    return null;
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="group mt-1 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
        <ChevronDown className="size-3 transition-transform group-data-[state=open]:rotate-180" />
        {open
          ? 'Hide contents'
          : `View ${item.components.length} included result${item.components.length === 1 ? '' : 's'}`}
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ul
          className={`mt-2 grid gap-1 border-l pl-3 text-xs text-muted-foreground ${
            item.components.length >= 5 ? 'sm:grid-cols-2' : ''
          }`}
        >
          {item.components.map((component) => (
            <li key={component.id}>{component.name}</li>
          ))}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  );
}

function markerToSelectionItem(
  marker: LabMarkerT
): NativeLabOrderSelectionItem & { source: 'marker' } {
  return {
    source: 'marker',
    id: marker.id,
    name: marker.name,
    kind:
      marker.metadata.type === 'panel'
        ? 'panel'
        : marker.metadata.type === 'biomarker'
          ? 'individual_test'
          : 'unknown',
    components: (marker.metadata.expected_results ?? []).map(({ id, name }) => ({ id, name })),
  };
}

export function NativeLabOrderSelectionStep({
  selectedItems,
  onAddItem,
  onRemoveItem,
}: NativeLabOrderSelectionStepProps) {
  const [query, setQuery] = useState('');
  const search = useBiomarkerSearch({ name: query });
  const results = search.data?.markers ?? [];
  const selectedMarkerIds = useMemo(
    () => new Set(selectedItems.filter(({ source }) => source === 'marker').map(({ id }) => id)),
    [selectedItems]
  );

  return (
    <div className="space-y-5">
      <section className="space-y-2" aria-labelledby="selected-tests-heading">
        <div className="flex items-baseline justify-between gap-3">
          <h3 id="selected-tests-heading" className="text-sm font-medium">
            Selected tests
          </h3>
          <span className="text-xs text-muted-foreground">{selectedItems.length} selected</span>
        </div>

        {selectedItems.length === 0 ? (
          <div className="rounded-lg border border-dashed px-4 py-5 text-center text-sm text-muted-foreground">
            Search below to add the tests needed for this patient.
          </div>
        ) : (
          <ul className="divide-y rounded-lg border">
            {selectedItems.map((item) => (
              <li key={`${item.source}:${item.id}`} className="flex gap-3 px-3 py-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium">{item.name}</p>
                    {item.kind === 'panel' || item.kind === 'configured_test' ? (
                      <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                        Panel
                      </span>
                    ) : null}
                  </div>
                  <Contents item={item} />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-8 shrink-0"
                  onClick={() => onRemoveItem(item)}
                  aria-label={`Remove ${item.name}`}
                >
                  <X className="size-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="space-y-3" aria-labelledby="find-tests-heading">
        <h3 id="find-tests-heading" className="text-sm font-medium">
          Add tests
        </h3>
        <div className="relative">
          <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground/60" />
          <Input
            autoFocus
            placeholder="Search tests and panels…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className="pl-9"
          />
        </div>

        {query.trim() === '' ? (
          <p className="text-sm text-muted-foreground">
            Search by test, panel, or analyte name. All results below are orderable through Quest.
          </p>
        ) : search.isLoading ? (
          <p className="text-sm text-muted-foreground" role="status">
            Searching Quest tests…
          </p>
        ) : search.isError ? (
          <p className="text-sm text-destructive">
            {search.error instanceof Error ? search.error.message : 'Could not search tests.'}
          </p>
        ) : results.length === 0 ? (
          <p className="text-sm text-muted-foreground">No matching Quest tests found.</p>
        ) : (
          <ul className="divide-y rounded-lg border">
            {results.map((marker) => {
              const item = markerToSelectionItem(marker);
              const selected = selectedMarkerIds.has(marker.id);
              const isPanel = marker.metadata.type === 'panel';
              return (
                <li key={marker.id} className="flex gap-3 px-3 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium">{marker.name}</p>
                      <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                        {isPanel ? 'Panel' : 'Test'}
                      </span>
                    </div>
                    <Contents item={item} />
                  </div>
                  <Button
                    type="button"
                    variant={selected ? 'ghost' : 'secondary'}
                    size="sm"
                    disabled={selected}
                    onClick={() => onAddItem(item)}
                    aria-label={selected ? `${marker.name} selected` : `Add ${marker.name}`}
                  >
                    {selected ? (
                      <Check className="mr-1 size-3.5" />
                    ) : (
                      <Plus className="mr-1 size-3.5" />
                    )}
                    {selected ? 'Added' : 'Add'}
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
